<template>
  <main class="growth-page">
    <header>
      <div>
        <h1>增长与计费策略</h1>
        <span>管理增长奖励和资源计费；套餐权益在商品中维护，所有修改均写入审计日志。</span>
      </div>
      <a-button @click="load"><i class="ri-refresh-line" />刷新</a-button>
    </header>
    <a-form layout="vertical">
      <a-tabs v-model:active-key="activeTab" class="policy-tabs">
        <a-tab-pane key="rewards" tab="增长奖励">
          <section class="tab-intro">
            <h2>增长奖励</h2>
            <p>签到和邀请奖励均在服务端结算，设置只影响后续奖励。</p>
          </section>
          <a-alert
            class="tab-notice"
            type="warning"
            show-icon
            message="启用前请确认奖励成本"
            description="前端参数不会直接发放积分，所有资格和到账均由服务端判定。"
          />
          <div class="reward-grid">
            <a-card title="每日签到">
              <a-form-item label="启用活动"
                ><a-switch v-model:checked="checkin.enabled"
              /></a-form-item>
              <a-form-item label="时区"><a-input v-model:value="checkin.timezone" /></a-form-item>
              <a-form-item label="7 日循环奖励（Credits，逗号分隔）"
                ><a-input v-model:value="checkinRewards"
              /></a-form-item>
              <a-form-item label="奖励有效期（天）"
                ><a-input-number v-model:value="checkin.rewardExpiryDays" :min="1" :max="365"
              /></a-form-item>
              <a-form-item label="变更原因"><a-input v-model:value="checkinReason" /></a-form-item>
              <a-button type="primary" :loading="saving === 'checkin'" @click="saveCheckin"
                >保存签到策略</a-button
              >
            </a-card>
            <a-card title="邀请裂变">
              <a-form-item label="启用活动"
                ><a-switch v-model:checked="referral.enabled"
              /></a-form-item>
              <a-form-item label="归因有效期（天）"
                ><a-input-number v-model:value="referral.attributionDays" :min="1" :max="90"
              /></a-form-item>
              <a-form-item label="邀请人奖励发放时机"
                ><a-select
                  v-model:value="referral.inviterRewardTrigger"
                  :options="[
                    { value: 'registration', label: '好友注册成功后' },
                    { value: 'first_ai_use', label: '好友首次成功使用 AI 后' },
                  ]"
              /></a-form-item>
              <a-form-item label="新人注册奖励"
                ><a-input-number v-model:value="referral.inviteeReward" :min="0" :max="100"
              /></a-form-item>
              <a-form-item label="邀请人有效邀请奖励"
                ><a-input-number v-model:value="referral.inviterReward" :min="0" :max="100"
              /></a-form-item>
              <a-form-item label="每日有效邀请上限"
                ><a-input-number v-model:value="referral.dailyQualifiedLimit" :min="1" :max="100"
              /></a-form-item>
              <a-form-item label="奖励有效期（天）"
                ><a-input-number v-model:value="referral.rewardExpiryDays" :min="1" :max="365"
              /></a-form-item>
              <a-form-item label="变更原因"><a-input v-model:value="referralReason" /></a-form-item>
              <a-button type="primary" :loading="saving === 'referral'" @click="saveReferral"
                >保存邀请策略</a-button
              >
            </a-card>
          </div>
        </a-tab-pane>

        <a-tab-pane key="metering" tab="资源计费">
          <section class="tab-intro">
            <h2>资源计费</h2>
            <p>设置资源与按次调用费率；模型 Token 费率在“系统模型”中管理。</p>
          </section>
          <a-card class="metering-card" title="资源计费率">
            <a-alert
              class="tab-notice"
              type="info"
              show-icon
              message="系统模型费率在「系统模型」中配置"
              description="此处配置 Embedding、Sandbox、Sandbox NAS、定时任务和 Web 搜索费率；暖机和空闲时间不计费。"
            />
            <a-form-item label="Embedding（每百万 Token 积分）"
              ><a-input-number
                v-model:value="metering.embeddingCreditsPerMillionTokens"
                :min="0"
                :precision="3"
            /></a-form-item>
            <a-form-item label="Sandbox（每活动小时积分）"
              ><a-input-number
                v-model:value="metering.sandboxCreditsPerActiveHour"
                :min="0"
                :precision="3"
            /></a-form-item>
            <a-form-item label="Sandbox NAS（每 GiB·月积分）"
              ><a-input-number
                v-model:value="metering.sandboxNasCreditsPerGiBMonth"
                :min="0"
                :precision="3"
            /></a-form-item>
            <a-form-item label="定时任务（每次触发积分）"
              ><a-input-number
                v-model:value="metering.scheduledTaskCreditsPerTrigger"
                :min="0"
                :precision="3"
            /></a-form-item>
            <a-form-item label="Web 搜索（每次调用积分）"
              ><a-input-number
                v-model:value="metering.webSearchCreditsPerCall"
                :min="0"
                :precision="3"
            /></a-form-item>
            <a-form-item label="变更原因"><a-input v-model:value="meteringReason" /></a-form-item>
            <a-button type="primary" :loading="saving === 'metering'" @click="saveMetering"
              >保存资源计费率</a-button
            >
          </a-card>
        </a-tab-pane>
      </a-tabs>
    </a-form>
  </main>
</template>

<script setup lang="ts">
import { computed, onMounted, reactive, ref } from 'vue';
import { message } from 'ant-design-vue';
import { adminApi } from '@/api';
import type { CheckInPolicy, ReferralPolicy } from 'shared';

const defaults = {
  checkin: {
    enabled: false,
    timezone: 'Asia/Shanghai',
    rewards: [1, 1, 1, 2, 2, 3, 5],
    rewardExpiryDays: 90,
  },
  referral: {
    enabled: false,
    attributionDays: 30,
    inviterRewardTrigger: 'registration' as const,
    inviteeReward: 10,
    inviterReward: 20,
    dailyQualifiedLimit: 10,
    rewardExpiryDays: 90,
  },
};
const checkin = reactive<CheckInPolicy>({ ...defaults.checkin });
const referral = reactive<ReferralPolicy>({ ...defaults.referral });
const metering = reactive({
  embeddingCreditsPerMillionTokens: 0,
  sandboxCreditsPerActiveHour: 0,
  sandboxNasCreditsPerGiBMonth: 0,
  scheduledTaskCreditsPerTrigger: 0,
  webSearchCreditsPerCall: 0,
});
const versions = reactive({ checkin: 0, referral: 0, metering: 0 });
const checkinReason = ref('');
const referralReason = ref('');
const meteringReason = ref('');
const saving = ref<'checkin' | 'referral' | 'metering' | null>(null);
const activeTab = ref('rewards');
const checkinRewards = computed({
  get: () => checkin.rewards.join(', '),
  set: (value: string) => {
    checkin.rewards = value
      .split(',')
      .map((item) => Number(item.trim()))
      .filter((item) => Number.isInteger(item) && item >= 0);
  },
});
async function load() {
  try {
    const items =
      await adminApi.get<Array<{ key: string; value: unknown; version: number }>>(
        '/admin/settings',
      );
    for (const item of items) {
      if (item.key === 'growth.checkin') {
        Object.assign(checkin, item.value as CheckInPolicy);
        versions.checkin = item.version;
      }
      if (item.key === 'growth.referral') {
        Object.assign(referral, item.value as ReferralPolicy);
        versions.referral = item.version;
      }
      if (item.key === 'billing.metering') {
        Object.assign(metering, item.value as typeof metering);
        versions.metering = item.version;
      }
    }
  } catch {
    message.error('增长与计费策略加载失败');
  }
}
async function save(
  key: 'checkin' | 'referral' | 'metering',
  value: CheckInPolicy | ReferralPolicy | typeof metering,
  reason: string,
) {
  if (!reason.trim()) {
    message.warning('请填写本次变更原因');
    return;
  }
  saving.value = key;
  const settingKey = key === 'metering' ? 'billing.metering' : `growth.${key}`;
  try {
    const result = await adminApi.patch<{ version: number }>(`/admin/settings/${settingKey}`, {
      value,
      version: versions[key],
      reason: reason.trim(),
    });
    versions[key] = result.version;
    message.success('策略已保存');
  } catch {
    message.error('保存失败，可能已被其他管理员修改');
  } finally {
    saving.value = null;
  }
}
function saveCheckin() {
  void save('checkin', { ...checkin, rewards: [...checkin.rewards] }, checkinReason.value);
}
function saveReferral() {
  void save('referral', { ...referral }, referralReason.value);
}
function saveMetering() {
  void save('metering', { ...metering }, meteringReason.value);
}
onMounted(() => {
  void load();
});
</script>

<style scoped>
.growth-page {
  max-width: 1180px;
  margin: 0 auto;
  padding: 32px;
}
.growth-page > header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 20px;
}
.growth-page h1 {
  margin: 0 0 7px;
  color: var(--aw-text);
  font-size: 28px;
  letter-spacing: -0.03em;
}
.growth-page header span {
  color: var(--aw-text-secondary);
}
.policy-tabs {
  margin-top: 20px;
}
.policy-tabs :deep(.ant-tabs-nav) {
  margin-bottom: 24px;
}
.policy-tabs :deep(.ant-tabs-tab) {
  padding: 10px 2px;
}
.tab-intro {
  margin-bottom: 16px;
}
.tab-intro h2 {
  margin: 0;
  color: var(--aw-text);
  font-size: 18px;
}
.tab-intro p {
  margin: 6px 0 0;
  color: var(--aw-text-secondary);
}
.quota-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 16px;
}
.quota-plan {
  padding: 18px;
  border: 1px solid var(--aw-border);
  border-radius: var(--aw-radius-md);
  background: var(--aw-surface);
}
.quota-plan h2 {
  margin: 0 0 16px;
  color: var(--aw-text);
  font-size: 16px;
}
.full-width {
  width: 100%;
}
.save-row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 16px;
  align-items: end;
  max-width: 760px;
  margin-top: 20px;
}
.save-row :deep(.ant-form-item) {
  margin-bottom: 0;
}
.tab-notice {
  margin-bottom: 16px;
}
.reward-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
}
.reward-grid :deep(.ant-card),
.metering-card {
  border-color: var(--aw-border);
  box-shadow: none;
}
.metering-card {
  max-width: 620px;
}
@media (max-width: 760px) {
  .growth-page {
    padding: 20px 14px;
  }
  .quota-grid,
  .reward-grid {
    grid-template-columns: 1fr;
  }
  .save-row {
    grid-template-columns: 1fr;
    align-items: stretch;
  }
  .growth-page > header {
    display: block;
  }
  .growth-page > header .ant-btn {
    margin-top: 12px;
  }
}
</style>
